(function () {
  'use strict';

  var GEOCODE_CACHE_KEY = 'otiumberg-stockist-geocode';
  var GEOCODE_DELAY_MS = 1100;
  var NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
  var MAPBOX_STYLE = 'mapbox://styles/mapbox/light-v11';
  var MAP_NEARBY_MAX_MILES = 35;
  var MAP_SEARCH_ZOOM = 11;
  var MAP_SEARCH_MAX_ZOOM = 13;
  var lastGeocodeRequest = 0;

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  function readCache() {
    try {
      return JSON.parse(sessionStorage.getItem(GEOCODE_CACHE_KEY) || '{}');
    } catch (error) {
      return {};
    }
  }

  function writeCache(cache) {
    try {
      sessionStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      /* ignore quota errors */
    }
  }

  function normalizeAddress(address) {
    return String(address || '').replace(/\s+/g, ' ').trim();
  }

  function waitForGeocodeSlot() {
    var elapsed = Date.now() - lastGeocodeRequest;
    var wait = Math.max(0, GEOCODE_DELAY_MS - elapsed);
    return new Promise(function (resolve) {
      window.setTimeout(resolve, wait);
    });
  }

  function isUkPostcode(value) {
    return /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(normalizeAddress(value));
  }

  function extractUkPostcode(address) {
    var match = normalizeAddress(address).match(/\b([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\b/i);
    return match ? match[1] : null;
  }

  function geocodeUkPostcode(postcode) {
    var normalized = normalizeAddress(postcode).replace(/\s+/g, '');

    return fetch('https://api.postcodes.io/postcodes/' + encodeURIComponent(normalized))
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (!data || data.status !== 200 || !data.result) {
          return null;
        }

        return {
          lat: parseFloat(data.result.latitude),
          lng: parseFloat(data.result.longitude)
        };
      })
      .catch(function () {
        return null;
      });
  }

  function geocodeMapbox(query, token) {
    var normalized = normalizeAddress(query);
    if (!normalized || !token) {
      return Promise.resolve(null);
    }

    var cache = readCache();
    var cacheKey = 'mapbox:' + normalized;
    if (cache[cacheKey]) {
      return Promise.resolve(cache[cacheKey]);
    }

    var url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/'
      + encodeURIComponent(normalized)
      + '.json?access_token='
      + encodeURIComponent(token)
      + '&limit=1';

    return fetch(url)
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Mapbox geocode failed');
        }
        return response.json();
      })
      .then(function (data) {
        if (!data || !data.features || !data.features.length) {
          return null;
        }

        var coords = {
          lng: parseFloat(data.features[0].center[0]),
          lat: parseFloat(data.features[0].center[1])
        };

        if (isNaN(coords.lat) || isNaN(coords.lng)) {
          return null;
        }

        cache[cacheKey] = coords;
        writeCache(cache);
        return coords;
      })
      .catch(function () {
        return null;
      });
  }

  function geocodeNominatim(query) {
    var normalized = normalizeAddress(query);
    if (!normalized) {
      return Promise.resolve(null);
    }

    var cache = readCache();
    var cacheKey = 'nominatim:' + normalized;
    if (cache[cacheKey]) {
      return Promise.resolve(cache[cacheKey]);
    }

    return waitForGeocodeSlot().then(function () {
      lastGeocodeRequest = Date.now();
      var url = NOMINATIM_URL + '?format=json&limit=1&addressdetails=0&q=' + encodeURIComponent(normalized);

      return fetch(url, {
        headers: {
          Accept: 'application/json'
        }
      }).then(function (response) {
        if (!response.ok) {
          throw new Error('Geocode failed');
        }
        return response.json();
      }).then(function (results) {
        if (!results || !results.length) {
          return null;
        }

        var coords = {
          lat: parseFloat(results[0].lat),
          lng: parseFloat(results[0].lon)
        };

        if (isNaN(coords.lat) || isNaN(coords.lng)) {
          return null;
        }

        cache[cacheKey] = coords;
        writeCache(cache);
        return coords;
      }).catch(function () {
        return null;
      });
    });
  }

  function geocodeQuery(query, token) {
    var normalized = normalizeAddress(query);
    if (!normalized) {
      return Promise.resolve(null);
    }

    var cache = readCache();
    if (cache[normalized]) {
      return Promise.resolve(cache[normalized]);
    }

    if (isUkPostcode(normalized)) {
      return geocodeUkPostcode(normalized).then(function (coords) {
        if (!coords) {
          return geocodeMapbox(normalized, token).then(function (mapboxCoords) {
            if (mapboxCoords) {
              cache[normalized] = mapboxCoords;
              writeCache(cache);
            }
            return mapboxCoords || geocodeNominatim(normalized);
          });
        }

        cache[normalized] = coords;
        writeCache(cache);
        return coords;
      });
    }

    return geocodeMapbox(normalized, token).then(function (coords) {
      if (coords) {
        cache[normalized] = coords;
        writeCache(cache);
        return coords;
      }
      return geocodeNominatim(normalized);
    });
  }

  function geocodeAddress(address, token) {
    var normalized = normalizeAddress(address);
    if (!normalized) {
      return Promise.resolve(null);
    }

    var cache = readCache();
    if (cache[normalized]) {
      return Promise.resolve(cache[normalized]);
    }

    var ukPostcode = extractUkPostcode(normalized);
    if (ukPostcode) {
      return geocodeUkPostcode(ukPostcode).then(function (coords) {
        if (coords) {
          cache[normalized] = coords;
          writeCache(cache);
          return coords;
        }
        return geocodeMapbox(normalized, token).then(function (mapboxCoords) {
          if (mapboxCoords) {
            cache[normalized] = mapboxCoords;
            writeCache(cache);
            return mapboxCoords;
          }
          return geocodeNominatim(normalized);
        });
      });
    }

    return geocodeMapbox(normalized, token).then(function (coords) {
      if (coords) {
        cache[normalized] = coords;
        writeCache(cache);
        return coords;
      }
      return geocodeNominatim(normalized);
    });
  }

  function reverseGeocode(coords, token) {
    if (!coords || !token) {
      return Promise.resolve('');
    }

    var cache = readCache();
    var cacheKey = 'reverse:' + coords.lat.toFixed(5) + ',' + coords.lng.toFixed(5);
    if (cache[cacheKey]) {
      return Promise.resolve(cache[cacheKey]);
    }

    var url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/'
      + encodeURIComponent(coords.lng + ',' + coords.lat)
      + '.json?access_token='
      + encodeURIComponent(token)
      + '&limit=1';

    return fetch(url)
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Reverse geocode failed');
        }
        return response.json();
      })
      .then(function (data) {
        if (!data || !data.features || !data.features.length) {
          return '';
        }

        var label = data.features[0].place_name || '';
        if (label) {
          cache[cacheKey] = label;
          writeCache(cache);
        }
        return label;
      })
      .catch(function () {
        return '';
      });
  }

  function haversineMiles(lat1, lng1, lat2, lng2) {
    var toRad = function (value) {
      return value * Math.PI / 180;
    };
    var earthRadiusMiles = 3958.8;
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
      + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2))
      * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusMiles * c;
  }

  function formatDistance(miles) {
    if (miles < 0.1) {
      return 'Less than 0.1 miles away';
    }
    if (miles < 10) {
      return miles.toFixed(1) + ' miles away';
    }
    return Math.round(miles) + ' miles away';
  }

  function createSearchMarkerElement() {
    var el = document.createElement('div');
    el.className = 'stockists-map-search-marker';
    el.setAttribute('aria-hidden', 'true');
    return el;
  }

  function createFlagshipMarkerElement(logoUrl) {
    var el = document.createElement('div');
    el.className = 'stockists-map-flagship-marker';
    el.setAttribute('aria-hidden', 'true');

    if (logoUrl) {
      var img = document.createElement('img');
      img.src = logoUrl;
      img.alt = '';
      img.width = 18;
      img.height = 18;
      el.appendChild(img);
    }

    return el;
  }

  function normalizeAddressForMatch(address) {
    return normalizeAddress(address)
      .toLowerCase()
      .replace(/,/g, '')
      .replace(/\./g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function cleanAddress(address) {
    return normalizeAddress(address)
      .replace(/,\s*,+/g, ', ')
      .replace(/\s+,/g, ',')
      .replace(/,\s*$/g, '')
      .replace(/^,\s*/g, '');
  }

  function buildStockistPopupHtml(title, address, distanceText) {
    var html = '<div class="stockists-map-popup">';
    html += '<div class="stockists-map-popup__header">';
    html += '<strong class="stockists-map-popup__title">' + escapeHtml(title) + '</strong>';
    html += '</div>';
    html += '<div class="stockists-map-popup__body">';

    if (address) {
      html += '<p class="stockists-map-popup__address">' + escapeHtml(cleanAddress(address)) + '</p>';
    }

    if (distanceText) {
      html += '<p class="stockists-map-popup__distance">' + escapeHtml(distanceText) + '</p>';
    }

    html += '<a class="stockists-map-popup__link" href="https://www.google.com/maps/dir/?api=1&destination='
      + encodeURIComponent(address || title)
      + '" target="_blank" rel="noopener">Directions</a>';
    html += '</div></div>';
    return html;
  }

  function StockistLocator(root) {
    this.root = root;
    this.mapboxToken = root.getAttribute('data-mapbox-token') || '';
    this.regionSelect = root.querySelector('#region-select');
    this.searchInput = root.querySelector('#stockist-search-input');
    this.searchButton = root.querySelector('#stockist-search-btn');
    this.locationButton = root.querySelector('#stockist-location-btn');
    this.searchMessage = root.querySelector('#stockist-search-message');
    this.toolbarActions = root.querySelector('.stockists-toolbar__actions');
    this.viewToggle = root.querySelector('#stockists-view-toggle');
    this.mapPanel = root.querySelector('#stockists-map-panel');
    this.mapElement = root.querySelector('#stockists-map');
    this.mapNotice = root.querySelector('#stockists-map-notice');
    this.results = root.querySelector('#stockists-results');
    this.sections = Array.prototype.slice.call(root.querySelectorAll('.stockists-section'));
    this.currentView = 'list';
    this.searchOrigin = null;
    this.map = null;
    this.mapMarkers = [];
    this.originalOrder = new Map();
    this.focusStockistEl = null;
    this.mapWheelHandlerBound = false;

    this.bindEvents();
    this.cacheOriginalOrder();
    this.markFlagshipStockists();
    this.updateToolbarState();
  }

  StockistLocator.prototype.getFlagship = function () {
    return {
      title: this.root.getAttribute('data-flagship-title') || '',
      address: this.root.getAttribute('data-flagship-address') || '',
      logoUrl: this.root.getAttribute('data-flagship-logo') || ''
    };
  };

  StockistLocator.prototype.isFlagshipStockist = function (stockistEl) {
    var flagship = this.getFlagship();

    if (!flagship.address || !stockistEl) {
      return false;
    }

    if (stockistEl.hasAttribute('data-stockist-flagship')) {
      return true;
    }

    var stockistAddress = stockistEl.getAttribute('data-address') || '';
    return normalizeAddressForMatch(stockistAddress) === normalizeAddressForMatch(flagship.address);
  };

  StockistLocator.prototype.markFlagshipStockists = function () {
    var self = this;
    var flagship = this.getFlagship();

    if (!flagship.address) {
      return;
    }

    this.sections.forEach(function (section) {
      self.getStockists(section).forEach(function (stockist) {
        if (self.isFlagshipStockist(stockist)) {
          stockist.classList.add('stockist--flagship');
          stockist.setAttribute('data-stockist-flagship', '');
        }
      });
    });
  };

  StockistLocator.prototype.addFlagshipMarker = function (flagship, coords, origin, bounds) {
    var distanceText = origin
      ? formatDistance(haversineMiles(origin.lat, origin.lng, coords.lat, coords.lng))
      : '';
    var popupHtml = buildStockistPopupHtml(flagship.title, flagship.address, distanceText);
    var marker = new window.mapboxgl.Marker({
      element: createFlagshipMarkerElement(flagship.logoUrl),
      anchor: 'center'
    })
      .setLngLat([coords.lng, coords.lat])
      .setPopup(new window.mapboxgl.Popup({
        offset: 14,
        maxWidth: '280px',
        className: 'stockists-map-popup-wrap stockists-map-popup-wrap--flagship'
      }).setHTML(popupHtml))
      .addTo(this.map);

    this.mapMarkers.push(marker);
    bounds.extend([coords.lng, coords.lat]);
    return marker;
  };

  StockistLocator.prototype.cacheOriginalOrder = function () {
    var self = this;
    this.sections.forEach(function (section) {
      var stockists = self.getStockists(section);
      self.originalOrder.set(section, stockists.slice());
    });
  };

  StockistLocator.prototype.bindEvents = function () {
    var self = this;

    if (this.regionSelect) {
      this.regionSelect.addEventListener('change', function () {
        self.onRegionChange();
      });
    }

    if (this.searchButton) {
      this.searchButton.addEventListener('click', function () {
        self.runSearch();
      });
    }

    if (this.searchInput) {
      this.searchInput.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          self.runSearch();
        }
      });
    }

    if (this.locationButton) {
      this.locationButton.addEventListener('click', function () {
        self.runSearchFromCurrentLocation();
      });
    }

    if (this.viewToggle) {
      this.viewToggle.addEventListener('click', function (event) {
        var button = event.target.closest('[data-view]');
        if (!button || button.disabled) return;
        self.setView(button.getAttribute('data-view'));
      });
    }

    this.root.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-stockist-map-link]');
      if (!trigger) return;
      event.preventDefault();
      var stockist = trigger.closest('[data-stockist]');
      if (stockist) {
        self.openStockistOnMap(stockist);
      }
    });
  };

  StockistLocator.prototype.getStockistContainer = function (section) {
    return section.querySelector('.stockists-grid') || section;
  };

  StockistLocator.prototype.getActiveSection = function () {
    return this.sections.find(function (section) {
      return section.style.display !== 'none';
    }) || this.sections[0];
  };

  StockistLocator.prototype.getStockists = function (section) {
    if (!section) return [];
    return Array.prototype.slice.call(section.querySelectorAll('[data-stockist]'));
  };

  StockistLocator.prototype.isMapSection = function (section) {
    return section && (section.id === 'stockists-uk' || section.id === 'stockists-eu');
  };

  StockistLocator.prototype.isMapEnabledRegion = function () {
    var region = this.regionSelect && this.regionSelect.value;
    return region === 'stockists-uk' || region === 'stockists-eu';
  };

  StockistLocator.prototype.isUkSection = function (section) {
    return section && section.id === 'stockists-uk';
  };

  StockistLocator.prototype.isOnlineSection = function (section) {
    return section && section.id === 'stockists-online';
  };

  StockistLocator.prototype.showMessage = function (message, isError) {
    if (!this.searchMessage) return;
    this.searchMessage.textContent = message || '';
    this.searchMessage.hidden = !message;
    this.searchMessage.classList.toggle('is-error', !!isError);
  };

  StockistLocator.prototype.showMapNotice = function (message) {
    if (!this.mapNotice) return;
    this.mapNotice.textContent = message || '';
    this.mapNotice.hidden = !message;
  };

  StockistLocator.prototype.setLoading = function (isLoading) {
    if (this.searchButton) {
      this.searchButton.disabled = isLoading;
      this.searchButton.textContent = isLoading ? 'Searching…' : 'Search';
    }

    if (this.locationButton) {
      this.locationButton.disabled = isLoading;
      this.locationButton.classList.toggle('is-loading', isLoading);
    }
  };

  StockistLocator.prototype.applySearchFromOrigin = function (origin) {
    var self = this;
    var activeSection = this.getActiveSection();

    this.searchOrigin = origin;

    return this.sortSectionByDistance(activeSection, origin).then(function (rankedCount) {
      if (rankedCount) {
        self.showMessage('Showing ' + rankedCount + ' stockist' + (rankedCount === 1 ? '' : 's') + ', nearest first.');
      } else {
        self.showMessage('No stockists could be matched to that location.', true);
      }

      if (self.currentView === 'map') {
        self.refreshMap(origin);
      }
    });
  };

  StockistLocator.prototype.runSearchFromCurrentLocation = function () {
    var self = this;
    var activeSection = this.getActiveSection();

    if (!this.isMapSection(activeSection)) {
      return;
    }

    if (!navigator.geolocation) {
      this.showMessage('Location is not supported in this browser.', true);
      return;
    }

    this.setLoading(true);
    this.showMessage('Getting your location…');

    navigator.geolocation.getCurrentPosition(function (position) {
      var origin = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };

      reverseGeocode(origin, self.mapboxToken).then(function (label) {
        if (self.searchInput) {
          self.searchInput.value = label || 'Current location';
        }

        self.showMessage('Finding stockists near you…');
        return self.applySearchFromOrigin(origin);
      }).finally(function () {
        self.setLoading(false);
      });
    }, function (error) {
      self.setLoading(false);

      if (error.code === 1) {
        self.showMessage('Location access was blocked. Enable location permissions to use this feature.', true);
        return;
      }

      if (error.code === 3) {
        self.showMessage('Finding your location took too long. Please try again.', true);
        return;
      }

      self.showMessage('We could not access your location. Please try again.', true);
    }, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 300000
    });
  };

  StockistLocator.prototype.onRegionChange = function () {
    var region = this.regionSelect.value;
    this.sections.forEach(function (section) {
      section.style.display = section.id === region ? '' : 'none';
      section.classList.toggle('is-active', section.id === region);
    });

    this.resetSearch(true);
    this.updateToolbarState();

    if (this.currentView === 'map' && this.isMapEnabledRegion()) {
      this.refreshMap();
    }
  };

  StockistLocator.prototype.updateToolbarState = function () {
    var mapEnabled = this.isMapEnabledRegion();

    this.root.classList.toggle('stockists-locator--map-enabled', mapEnabled);

    if (this.toolbarActions) {
      this.toolbarActions.hidden = !mapEnabled;
    }

    if (!mapEnabled) {
      this.showMessage('');

      if (this.currentView === 'map') {
        this.setView('list');
      }

      if (this.mapPanel) {
        this.mapPanel.hidden = true;
      }

      this.root.classList.remove('stockists-locator--map-view');
    }
  };

  StockistLocator.prototype.openStockistOnMap = function (stockistEl) {
    var activeSection = this.getActiveSection();

    if (!this.isMapSection(activeSection)) {
      return;
    }

    if (!stockistEl || stockistEl.hasAttribute('data-no-location')) {
      return;
    }

    this.focusStockistEl = stockistEl;

    if (this.currentView !== 'map') {
      this.setView('map');
    } else {
      this.refreshMap(this.searchOrigin);
    }
  };

  StockistLocator.prototype.setView = function (view) {
    this.currentView = view === 'map' ? 'map' : 'list';
    this.root.classList.toggle('stockists-locator--map-view', this.currentView === 'map');

    if (this.currentView === 'list') {
      this.focusStockistEl = null;
    }

    if (this.mapPanel) {
      this.mapPanel.hidden = this.currentView !== 'map';
    }

    Array.prototype.forEach.call(this.viewToggle.querySelectorAll('[data-view]'), function (button) {
      button.classList.toggle('is-active', button.getAttribute('data-view') === this.currentView);
    }, this);

    if (this.currentView === 'map') {
      this.refreshMap(this.searchOrigin);
    } else {
      this.showMapNotice('');
    }
  };

  StockistLocator.prototype.resetSearch = function (clearInput) {
    var self = this;

    if (clearInput && this.searchInput) {
      this.searchInput.value = '';
    }

    this.searchOrigin = null;
    this.showMessage('');

    this.sections.forEach(function (section) {
      var container = self.getStockistContainer(section);
      var stockists = self.originalOrder.get(section) || self.getStockists(section);

      stockists.forEach(function (stockist) {
        stockist.hidden = false;
        stockist.style.order = '';
        stockist.removeAttribute('data-distance');
        var distanceEl = stockist.querySelector('.stockist__distance');
        if (distanceEl) {
          distanceEl.hidden = true;
          distanceEl.textContent = '';
        }
        container.appendChild(stockist);
      });
    });
  };

  StockistLocator.prototype.runSearch = function () {
    var self = this;
    var query = this.searchInput ? this.searchInput.value.trim() : '';
    var activeSection = this.getActiveSection();

    if (!this.isMapSection(activeSection)) {
      return;
    }

    if (!query) {
      this.resetSearch(false);
      if (this.currentView === 'map') {
        this.refreshMap();
      }
      return;
    }

    this.setLoading(true);
    this.showMessage('Finding stockists near you…');

    geocodeQuery(query, this.mapboxToken).then(function (origin) {
      if (!origin) {
        self.setLoading(false);
        self.showMessage('We could not find that location. Try a postcode, city, or address.', true);
        self.filterByText(activeSection, query);
        if (self.currentView === 'map') {
          self.refreshMap();
        }
        return;
      }

      self.searchOrigin = origin;
      return self.applySearchFromOrigin(origin);
    }).finally(function () {
      self.setLoading(false);
    });
  };

  StockistLocator.prototype.filterByText = function (section, query) {
    var normalizedQuery = query.toLowerCase();

    this.getStockists(section).forEach(function (stockist) {
      var address = (stockist.getAttribute('data-address') || '').toLowerCase();
      var title = (stockist.getAttribute('data-title') || '').toLowerCase();
      var matches = address.indexOf(normalizedQuery) !== -1 || title.indexOf(normalizedQuery) !== -1;
      stockist.hidden = !matches;
      var distanceEl = stockist.querySelector('.stockist__distance');
      if (distanceEl) {
        distanceEl.hidden = true;
        distanceEl.textContent = '';
      }
    });
  };

  StockistLocator.prototype.sortSectionByDistance = function (section, origin) {
    var self = this;
    var stockists = this.getStockists(section).filter(function (stockist) {
      return !stockist.hasAttribute('data-no-location');
    });

    var jobs = stockists.map(function (stockist) {
      var address = stockist.getAttribute('data-address');
      return geocodeAddress(address, self.mapboxToken).then(function (coords) {
        return {
          stockist: stockist,
          coords: coords
        };
      });
    });

    return Promise.all(jobs).then(function (results) {
      var container = self.getStockistContainer(section);
      var ranked = results
        .filter(function (item) {
          return item.coords;
        })
        .map(function (item) {
          return {
            stockist: item.stockist,
            coords: item.coords,
            distance: haversineMiles(origin.lat, origin.lng, item.coords.lat, item.coords.lng)
          };
        })
        .sort(function (a, b) {
          return a.distance - b.distance;
        });

      var unranked = results.filter(function (item) {
        return !item.coords;
      });

      ranked.forEach(function (item, index) {
        var distanceEl = item.stockist.querySelector('.stockist__distance');
        if (distanceEl) {
          distanceEl.textContent = formatDistance(item.distance);
          distanceEl.hidden = false;
        }
        item.stockist.hidden = false;
        item.stockist.style.order = String(index);
        item.stockist.setAttribute('data-distance', String(item.distance));
        container.appendChild(item.stockist);
      });

      unranked.forEach(function (item, index) {
        var distanceEl = item.stockist.querySelector('.stockist__distance');
        if (distanceEl) {
          distanceEl.hidden = true;
          distanceEl.textContent = '';
        }
        item.stockist.hidden = false;
        item.stockist.style.order = String(ranked.length + index);
        item.stockist.removeAttribute('data-distance');
        container.appendChild(item.stockist);
      });

      return ranked.length;
    });
  };

  StockistLocator.prototype.clearMapMarkers = function () {
    if (!this.mapMarkers.length) return;
    this.mapMarkers.forEach(function (marker) {
      marker.remove();
    });
    this.mapMarkers = [];
  };

  StockistLocator.prototype.ensureMap = function () {
    var self = this;

    if (!this.mapboxToken) {
      this.showMapNotice('Add your Mapbox access token in Theme settings > Stockists to enable the map.');
      return false;
    }

    if (typeof window.mapboxgl === 'undefined') {
      this.showMapNotice('Map is still loading. Please try again in a moment.');
      return false;
    }

    if (this.map) {
      return true;
    }

    window.mapboxgl.accessToken = this.mapboxToken;
    this.map = new window.mapboxgl.Map({
      container: this.mapElement,
      style: MAPBOX_STYLE,
      center: [-3.5, 54.5],
      zoom: 5,
      attributionControl: true,
      cooperativeGestures: false,
      dragPan: true,
      scrollZoom: true,
      boxZoom: true,
      doubleClickZoom: true,
      touchZoomRotate: true,
      keyboard: true
    });

    this.map.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

    this.map.on('load', function () {
      self.map.resize();
      self.map.scrollZoom.enable();
      self.map.dragPan.enable();
      self.map.touchZoomRotate.enable();
      self.map.boxZoom.enable();
      self.map.doubleClickZoom.enable();
      self.map.keyboard.enable();

      var canvasContainer = self.map.getCanvasContainer();
      if (canvasContainer && !self.mapWheelHandlerBound) {
        canvasContainer.addEventListener('wheel', function (event) {
          event.preventDefault();
        }, { passive: false });
        self.mapWheelHandlerBound = true;
      }
    });

    this.map.on('dragstart', function () {
      self.mapElement.classList.add('is-dragging');
    });

    this.map.on('dragend', function () {
      self.mapElement.classList.remove('is-dragging');
    });

    this.showMapNotice('');
    return true;
  };

  StockistLocator.prototype.refreshMap = function (origin) {
    var self = this;

    if (!this.ensureMap()) {
      return;
    }

    origin = origin || this.searchOrigin || null;

    var activeSection = this.getActiveSection();
    var focusEl = this.focusStockistEl;
    var stockists = this.getStockists(activeSection).filter(function (stockist) {
      return !stockist.hidden && !stockist.hasAttribute('data-no-location');
    });

    if (focusEl) {
      stockists = [focusEl];
      origin = null;
    }

    this.clearMapMarkers();

    var jobs = stockists.map(function (stockist) {
      return geocodeAddress(stockist.getAttribute('data-address'), self.mapboxToken).then(function (coords) {
        return {
          stockist: stockist,
          coords: coords
        };
      });
    });

    Promise.all(jobs).then(function (results) {
      var plotted = results.filter(function (item) {
        return item.coords;
      });

      if (origin) {
        plotted = plotted.map(function (item) {
          return {
            stockist: item.stockist,
            coords: item.coords,
            distance: haversineMiles(origin.lat, origin.lng, item.coords.lat, item.coords.lng)
          };
        }).filter(function (item) {
          return item.distance <= MAP_NEARBY_MAX_MILES;
        }).sort(function (a, b) {
          return a.distance - b.distance;
        });
      }

      var bounds = new window.mapboxgl.LngLatBounds();
      var hasBounds = false;
      var focusedMarker = null;
      var focusedCoords = null;

      if (origin) {
        var searchMarker = new window.mapboxgl.Marker({
          element: createSearchMarkerElement(),
          anchor: 'center'
        })
          .setLngLat([origin.lng, origin.lat])
          .addTo(self.map);
        self.mapMarkers.push(searchMarker);
        bounds.extend([origin.lng, origin.lat]);
        hasBounds = true;
      }

      plotted.forEach(function (item) {
        if (self.isFlagshipStockist(item.stockist)) {
          return;
        }

        var title = item.stockist.getAttribute('data-title') || 'Stockist';
        var address = item.stockist.getAttribute('data-address') || '';
        var distanceText = typeof item.distance === 'number'
          ? formatDistance(item.distance)
          : '';
        var distanceEl = item.stockist.querySelector('.stockist__distance');
        if (!distanceText && distanceEl && !distanceEl.hidden) {
          distanceText = distanceEl.textContent;
        }

        var popupHtml = buildStockistPopupHtml(title, address, distanceText);

        var marker = new window.mapboxgl.Marker({ color: '#111111', scale: 0.6 })
          .setLngLat([item.coords.lng, item.coords.lat])
          .setPopup(new window.mapboxgl.Popup({
            offset: 12,
            maxWidth: '280px',
            className: 'stockists-map-popup-wrap'
          }).setHTML(popupHtml))
          .addTo(self.map);

        self.mapMarkers.push(marker);
        bounds.extend([item.coords.lng, item.coords.lat]);
        hasBounds = true;

        if (focusEl && item.stockist === focusEl) {
          focusedMarker = marker;
          focusedCoords = item.coords;
        }
      });

      var flagship = self.getFlagship();
      var shouldPlotFlagship = flagship.address
        && self.isUkSection(activeSection)
        && (!focusEl || self.isFlagshipStockist(focusEl));

      var finishMapUpdate = function () {
        window.setTimeout(function () {
          self.map.resize();

          if (focusEl) {
            if (focusedCoords) {
              self.map.flyTo({
                center: [focusedCoords.lng, focusedCoords.lat],
                zoom: 14,
                duration: 600
              });

              if (focusedMarker) {
                window.setTimeout(function () {
                  focusedMarker.togglePopup();
                }, 650);
              }
            } else {
              self.showMapNotice('We could not locate this stockist on the map.');
            }

            self.focusStockistEl = null;
            return;
          }

          if (origin) {
            if (plotted.length || shouldPlotFlagship) {
              self.map.fitBounds(bounds, {
                padding: 72,
                maxZoom: MAP_SEARCH_MAX_ZOOM,
                duration: 600
              });
            } else {
              self.map.flyTo({
                center: [origin.lng, origin.lat],
                zoom: MAP_SEARCH_ZOOM,
                duration: 600
              });
            }
            return;
          }

          if (hasBounds && !bounds.isEmpty()) {
            self.map.fitBounds(bounds, { padding: 48, maxZoom: 12, duration: 0 });
          } else {
            self.map.setCenter([-3.5, 54.5]);
            self.map.setZoom(5);
          }
        }, 100);
      };

      if (shouldPlotFlagship) {
        geocodeAddress(flagship.address, self.mapboxToken).then(function (coords) {
          if (coords) {
            var flagshipMarker = self.addFlagshipMarker(flagship, coords, origin, bounds);
            hasBounds = true;

            if (focusEl && self.isFlagshipStockist(focusEl)) {
              focusedMarker = flagshipMarker;
              focusedCoords = coords;
            }
          }
          finishMapUpdate();
        });
      } else {
        finishMapUpdate();
      }
    });
  };

  ready(function () {
    var root = document.getElementById('stockists-locator');
    if (!root) return;

    var locator = new StockistLocator(root);
    var activeSection = locator.getActiveSection();
    if (activeSection) {
      activeSection.classList.add('is-active');
    }

    if (locator.regionSelect) {
      locator.onRegionChange();
    }
  });
})();
